import { zodResolver } from '@hookform/resolvers/zod';
import {
  AspectRatio,
  ContentLanguage,
  ImageModel,
  type WorkspaceDto,
} from '@reelplicate/api-client/model';
import {
  getListWorkspacesQueryKey,
  useListWorkspaces,
  useUpdateWorkspace,
} from '@reelplicate/api-client/workspaces';
import { Button, Combobox, Field, Stack, TextInput, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { siteHost } from '@/features/onboarding/api-values';
import { SiteInput } from '@/features/onboarding/favicon';

import { SettingSelect, SettingToggle } from './controls';
import { DeleteWorkspaceDialog } from './delete-workspace-dialog';
import {
  SettingsCard,
  SettingsPageLayout,
  SettingsRow,
  SettingsSection,
} from './settings-layout';
import { timezoneOptions } from './timezones';
import { WorkspaceLogo } from './workspace-logo';

const WEBSITE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;

const GeneralSchema = z.object({
  name: z.string().trim().min(1, 'Name your workspace'),
  website: z
    .string()
    .trim()
    .refine((v) => v === '' || WEBSITE.test(v), 'Enter a website like oatandember.com'),
  defaultAspectRatio: z.enum(AspectRatio),
  defaultImageModel: z.enum(ImageModel),
  timezone: z.string().min(1),
  language: z.enum(ContentLanguage),
});
type GeneralValues = z.infer<typeof GeneralSchema>;

const ASPECT_RATIOS: [AspectRatio, string][] = [
  ['VERTICAL', 'Vertical 9:16'],
  ['SQUARE', 'Square 1:1'],
  ['PORTRAIT', 'Portrait 4:5'],
  ['LANDSCAPE', 'Landscape 16:9'],
];

const IMAGE_MODELS: [ImageModel, string][] = [
  ['GPT_IMAGE', 'ChatGPT (GPT Image)'],
  ['NANO_BANANA', 'Gemini (Nano Banana)'],
];

const LANGUAGES: [ContentLanguage, string][] = [
  ['EN_GB', 'English (UK)'],
  ['EN_US', 'English (US)'],
  ['FR', 'French'],
  ['ES', 'Spanish'],
  ['DE', 'German'],
];

type Toggle = 'requireReview' | 'autoPublish' | 'crossPost';

const toValues = (w: WorkspaceDto): GeneralValues => ({
  name: w.name,
  website: siteHost(w.website),
  defaultAspectRatio: w.defaultAspectRatio,
  defaultImageModel: w.defaultImageModel,
  timezone: w.timezone,
  language: w.language,
});

/**
 * Figma "Settings — General" (320:2). Name, website and the Defaults save
 * together with "Save changes"; the Publishing toggles apply instantly (Figma
 * Settings Row). The logo uploads to storage; Delete workspace sends the
 * owner back to onboarding. Transfer ownership waits for real teammates.
 */
export function GeneralPage() {
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const update = useUpdateWorkspace();
  const toggle = useUpdateWorkspace({ mutation: { meta: { silent: true } } });
  const queryClient = useQueryClient();
  const timezones = useMemo(() => timezoneOptions(), []);
  const form = useForm<GeneralValues>({
    resolver: zodResolver(GeneralSchema),
    defaultValues: {
      name: '',
      website: '',
      defaultAspectRatio: 'VERTICAL',
      defaultImageModel: 'GPT_IMAGE',
      timezone: 'UTC',
      language: 'EN_GB',
    },
  });

  useEffect(() => {
    if (workspace && !form.formState.isDirty) form.reset(toValues(workspace));
  }, [workspace, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!workspace) return;
    let saved: WorkspaceDto;
    try {
      saved = await update.mutateAsync({ id: workspace.id, data: values });
    } catch {
      return; // The failure is already shown as a toast.
    }
    form.reset(toValues(saved));
    await queryClient.invalidateQueries({ queryKey: getListWorkspacesQueryKey() });
    toast.success('Changes saved');
  });

  /** Flip a publishing setting now: shown at once, put back if saving fails. */
  const setToggle = async (key: Toggle, value: boolean) => {
    if (!workspace) return;
    const queryKey = getListWorkspacesQueryKey();
    const patch = (next: boolean) =>
      queryClient.setQueryData<WorkspaceDto[]>(queryKey, (list) =>
        list?.map((w) => (w.id === workspace.id ? { ...w, [key]: next } : w)),
      );
    patch(value);
    try {
      await toggle.mutateAsync({ id: workspace.id, data: { [key]: value } });
      toast.success('Saved');
    } catch {
      patch(!value);
      toast.error('Couldn’t save that setting. Try again.');
    }
  };

  const name = useWatch({ control: form.control, name: 'name' }) || workspace?.name || '';

  return (
    <SettingsPageLayout
      title="General"
      description={`How ${workspace?.name ?? 'your workspace'} looks to your team, and what happens by default.`}
      onSubmit={onSubmit}
      actions={
        <Button
          type="submit"
          disabled={!form.formState.isDirty}
          loading={update.isPending}
        >
          Save changes
        </Button>
      }
    >
      <SettingsSection
        title="Workspace"
        description="Name, logo and website your teammates see."
      >
        <SettingsCard>
          {workspace && <WorkspaceLogo workspace={workspace} name={name} />}
          <Stack gap={4} className="pt-5 pb-6 sm:flex-row sm:items-start">
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  label="Workspace name"
                  helper="Shown to teammates and on invites."
                  error={fieldState.error?.message}
                  className="flex-1"
                >
                  <TextInput {...field} placeholder="e.g. Oat & Ember" />
                </Field>
              )}
            />
            <Controller
              name="website"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  label="Website"
                  helper="Used to refresh your brand profile."
                  error={fieldState.error?.message}
                  className="flex-1"
                >
                  <SiteInput
                    {...field}
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    placeholder="oatandember.com"
                  />
                </Field>
              )}
            />
          </Stack>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Defaults"
        description="Applied to every new reel and flow. People can still change them per reel."
      >
        <SettingsCard>
          <Controller
            name="defaultAspectRatio"
            control={form.control}
            render={({ field }) => (
              <SettingSelect
                label="Default aspect ratio"
                description="Used for new reels and templates."
                value={field.value}
                onValueChange={field.onChange}
                options={ASPECT_RATIOS}
              />
            )}
          />
          <Controller
            name="defaultImageModel"
            control={form.control}
            render={({ field }) => (
              <SettingSelect
                label="AI image model"
                description="Makes AI images in flows, unless a step picks its own."
                value={field.value}
                onValueChange={field.onChange}
                options={IMAGE_MODELS}
              />
            )}
          />
          <Controller
            name="timezone"
            control={form.control}
            render={({ field }) => (
              <SettingsRow
                label="Timezone"
                description="Schedules and reports use this time."
                control={
                  <Combobox
                    options={timezones}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Search timezones"
                    emptyTitle={(query) => `No timezone matches “${query}”`}
                    aria-label="Timezone"
                    className="w-60"
                  />
                }
              />
            )}
          />
          <Controller
            name="language"
            control={form.control}
            render={({ field }) => (
              <SettingSelect
                label="Language"
                description="Captions and on-screen text are written in this language."
                value={field.value}
                onValueChange={field.onChange}
                options={LANGUAGES}
              />
            )}
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Publishing"
        description="What happens after a render finishes."
      >
        <SettingsCard>
          <SettingToggle
            label="Require review before publishing"
            description="Drafts wait in “Needs your review” until an owner or reviewer approves them."
            checked={workspace?.requireReview ?? true}
            onCheckedChange={(on) => void setToggle('requireReview', on)}
            disabled={!workspace}
          />
          <SettingToggle
            label="Auto-publish approved reels"
            description="Approved reels go out at their scheduled time without another click."
            checked={workspace?.autoPublish ?? true}
            onCheckedChange={(on) => void setToggle('autoPublish', on)}
            disabled={!workspace}
          />
          <SettingToggle
            label="Cross-post to every connected account"
            description="Send each reel to TikTok, Instagram and YouTube at once."
            checked={workspace?.crossPost ?? false}
            onCheckedChange={(on) => void setToggle('crossPost', on)}
            disabled={!workspace}
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Danger zone"
        description="These actions can’t be undone."
        danger
      >
        <SettingsCard danger>
          <SettingsRow
            label="Transfer ownership"
            description="Invite a teammate first — you can transfer ownership once someone else has joined."
            disabled
            control={
              <Button type="button" variant="outline" size="sm" disabled>
                Transfer
              </Button>
            }
          />
          <SettingsRow
            label="Delete workspace"
            description="Permanently deletes every reel, connected account and all brand data for this workspace."
            control={
              workspace && (
                <DeleteWorkspaceDialog id={workspace.id} name={workspace.name} />
              )
            }
          />
        </SettingsCard>
      </SettingsSection>
    </SettingsPageLayout>
  );
}
